import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Play, Users, BookOpen, Zap, Check, AlertCircle, Calendar, MessageSquare, Star, Quote, Map, Headphones, GraduationCap, Globe, Target, Compass, ChevronDown } from 'lucide-react';

const Landing = () => {
  // State for collapsible cards in Section 2
  const [expandedCards, setExpandedCards] = useState({});
  
  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  // Video URLs for feature sections
  const featureVideos = {
    courses: 'https://customer-assets.emergentagent.com/job_blendonics-app/artifacts/256g8myh_LAPTOP%20MAIN%20MOCKUP.mp4',
    community: 'https://customer-assets.emergentagent.com/job_blendonics-app/artifacts/dn4mj5su_COMMUNITY.mp4',
    resources: 'https://customer-assets.emergentagent.com/job_blendonics-app/artifacts/4ed2m9hu_LAPTOP%20EBOOK%20MOCKUP.mp4'
  };

  // Hero background video
  const heroVideo = 'https://customer-assets.emergentagent.com/job_blendonics-app/artifacts/thc1269b_Shopify%20Blendonics%20Website%20Vid%20%28Compressed%29%20-%20.mp4';

  // Background images for sections
  const bgImages = {
    painPoints: 'https://customer-assets.emergentagent.com/job_blendonics-app/artifacts/t6qfg9c8_dj-board-volume-slider.jpg',
    memberships: 'https://customer-assets.emergentagent.com/job_mixlab-6/artifacts/qneg2hss_dj-board-volume-slider.jpg'
  };

  // Testimonials data
  const testimonials = [
    {
      name: "Sarah.",
      role: "Wedding DJ, Manchester",
      quote: "The community support is incredible. Getting feedback on my mixes helped me improve faster than I ever thought possible.",
      rating: 5
    },
    {
      name: "James R.",
      role: "Beginner DJ",
      quote: "I went from complete beginner to mixing at house parties in just 30 days. The lessons made all the difference.",
      rating: 5
    },
    {
      name: "Marcus T.",
      role: "Club DJ, London",
      quote: "This is a Must Have!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen" style={{ forcedColorAdjust: 'none' }}>
      {/* SECTION 1: HERO with Video Background */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.4)' }}
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        </div>
        {/* Subtle overlay for text readability */}
        <div className="absolute inset-0 z-1" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.5))' }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title" style={{ color: '#ffffff' }}>
              A Smarter Way to
              <br />
              <span className="text-gradient" style={{ background: 'linear-gradient(to right, rgb(58, 0, 136), rgb(212, 175, 55))', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Learn How To DJ</span>
            </h1>
            <p className="text-base sm:text-lg mb-8 max-w-2xl mx-auto" style={{ color: '#e5e5e5' }}>
              Join hundreds of DJs worldwide learning through our structured course, premium resources and a focused online DJ community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" data-testid="hero-cta">
                <Button size="lg" className="rounded-full w-full sm:w-auto transition-all duration-300" style={{ background: '#D4AF37', color: '#141414', boxShadow: '0 10px 40px rgba(212, 175, 55, 0.5)', fontWeight: '600' }}>
                  <Play className="mr-2" size={20} />
                  Start Your Journey
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-pricing">
                <Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto border-2" style={{ borderColor: '#D4AF37', color: '#ffffff' }}>
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: PAIN POINTS - with background image */}
      <section className="py-20 relative" data-testid="pain-points-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('${bgImages.painPoints}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.45
          }}
        />
        <div className="absolute inset-0 z-0" style={{ background: 'linear-gradient(to bottom, rgba(20,20,20,0.9), rgba(20,20,20,0.95))' }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-lg mb-4" style={{ color: '#a3a3a3' }}>
                We understand the challenges aspiring DJs face because we've been there too
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: "Overwhelmed by content?",
                  description: "Jumping between scattered tutorials with no clear learning path leads to confusion, slow progress and wasted practice time."
                },
                {
                  title: "Lack of structure?",
                  description: "Without a defined roadmap, it's easy to stall at beginner level and never develop consistent, performance-ready skills."
                },
                {
                  title: "Struggling to find the right music?",
                  description: "Without a clear system for finding, selecting and organising tracks, your DJ sets can feel unstructured, confidence drops and your progress slows."
                },
                {
                  title: "No access to feedback?",
                  description: "Practicing in isolation can reinforce bad habits, limit growth and leave you unsure if you're improving correctly."
                }
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="rounded-xl border border-border bg-card/80 backdrop-blur-sm p-6 transition-all duration-300 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20"
                  style={{ transformOrigin: 'center' }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <AlertCircle size={22} className="mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <h4 className="font-bold text-lg" style={{ color: '#ffffff' }}>{item.title}</h4>
                  </div>
                  <p style={{ color: '#a3a3a3' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: OUR STRUCTURED LEARNING SYSTEM (with video cards) */}
      <section className="py-20 bg-card/30" data-testid="solution-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>Our Structured Learning System</h2>
              <p className="max-w-3xl mx-auto text-lg" style={{ color: '#a3a3a3' }}>
                Unlike free content scattered across the internet, our platform provides a clear, proven path to becoming a talented DJ. It includes:
              </p>
            </div>
            
            {/* Video Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Structured Courses */}
              <div className="group relative overflow-hidden rounded-2xl border-2 bg-card transition-all duration-300 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20 hover:border-yellow-500/50" style={{ borderColor: '#262626' }} data-testid="feature-card-1">
                <div className="aspect-video overflow-hidden">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-full object-cover"
                    src={featureVideos.courses}
                  />
                </div>
                <div className="p-8">
                  <div className="mb-4">
                    <BookOpen size={32} style={{ color: '#D4AF37' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Structured Courses</h3>
                  <p style={{ color: '#a3a3a3' }}>
                    From beginner fundamentals to advanced techniques, learn at your own pace with video lessons and interactive challenges.
                  </p>
                </div>
              </div>
              {/* Exclusive Community */}
              <div className="group relative overflow-hidden rounded-2xl border-2 bg-card transition-all duration-300 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20 hover:border-yellow-500/50" style={{ borderColor: '#262626' }} data-testid="feature-card-2">
                <div className="aspect-video overflow-hidden">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-full object-cover"
                    src={featureVideos.community}
                  />
                </div>
                <div className="p-8">
                  <div className="mb-4">
                    <Users size={32} style={{ color: '#D4AF37' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Exclusive Community</h3>
                  <p style={{ color: '#a3a3a3' }}>
                    Connect with fellow DJs, share your mixes, get feedback, and stay motivated with a supportive community.
                  </p>
                </div>
              </div>
              {/* Premium Resources */}
              <div className="group relative overflow-hidden rounded-2xl border-2 bg-card transition-all duration-300 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20 hover:border-yellow-500/50" style={{ borderColor: '#262626' }} data-testid="feature-card-3">
                <div className="aspect-video overflow-hidden">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-full object-cover"
                    src={featureVideos.resources}
                  />
                </div>
                <div className="p-8">
                  <div className="mb-4">
                    <Zap size={32} style={{ color: '#D4AF37' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Premium Resources</h3>
                  <p style={{ color: '#a3a3a3' }}>
                    Access SFX packs, eBooks, sample libraries, and exclusive tools to elevate your DJ sets.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: OUR MEMBERSHIPS - with background image */}
      <section className="py-20 relative" data-testid="memberships-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('${bgImages.memberships}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.45
          }}
        />
        <div className="absolute inset-0 z-0" style={{ background: 'linear-gradient(to bottom, rgba(20,20,20,0.92), rgba(20,20,20,0.95))' }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>Our Memberships</h2>
              <p className="max-w-3xl mx-auto text-lg" style={{ color: '#a3a3a3' }}>
                Our platform contains everything you need to transform from beginner to confident DJ. Our three membership options (Starter, Pro and Elite) allow you to:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Global Community */}
              <div className="rounded-2xl border-2 bg-card/80 backdrop-blur-sm p-8 text-center transition-all duration-300 hover:border-yellow-500/50 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20" style={{ borderColor: '#262626' }}>
                <div className="inline-flex p-4 rounded-full mb-6" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                  <Users size={40} style={{ color: '#D4AF37' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Join our Global Community</h3>
                <p style={{ color: '#a3a3a3' }}>
                  Connect with DJs worldwide, share milestones, your DJ mixes and get detailed feedback.
                </p>
              </div>
              
              {/* Ongoing Support */}
              <div className="rounded-2xl border-2 bg-card/80 backdrop-blur-sm p-8 text-center transition-all duration-300 hover:border-yellow-500/50 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20" style={{ borderColor: '#262626' }}>
                <div className="inline-flex p-4 rounded-full mb-6" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                  <MessageSquare size={40} style={{ color: '#D4AF37' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Unlock Ongoing Support</h3>
                <p style={{ color: '#a3a3a3' }}>
                  Participate in discussions, receive updates and continuous learning opportunities.
                </p>
              </div>
              
              {/* 1:1 Coaching */}
              <div className="rounded-2xl border-2 bg-card/80 backdrop-blur-sm p-8 text-center transition-all duration-300 hover:border-yellow-500/50 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20" style={{ borderColor: '#262626' }}>
                <div className="inline-flex p-4 rounded-full mb-6" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                  <Calendar size={40} style={{ color: '#D4AF37' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#ffffff' }}>Book a 1:1 Monthly DJ Coaching Call</h3>
                <p style={{ color: '#a3a3a3' }}>
                  Schedule a call 15 - 20 Minutes on days that suit you to create your personal DJ improvement plan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: BEST-SELLING COURSE */}
      <section className="py-20 bg-card/30" data-testid="best-selling-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>Our Best-Selling 30 Day Beginner DJ Course</h2>
            </div>
            <div className="rounded-3xl border-2 overflow-hidden" style={{ borderColor: '#D4AF37', background: '#0a0a0a', boxShadow: '0 0 60px rgba(212, 175, 55, 0.3)' }}>
              <div className="p-8 md:p-12" style={{ background: '#0a0a0a' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  {/* Image - trimmed top and bottom */}
                  <div className="flex justify-center overflow-hidden" style={{ maxHeight: '420px' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mixlab-6/artifacts/nykg1fpo_mockup-of-a-macbook-floating-on-a-surface-with-two-colors-a20461.png" 
                      alt="DJ Course Preview" 
                      className="w-full max-w-md object-cover"
                      style={{ filter: 'drop-shadow(0 20px 40px rgba(212, 175, 55, 0.2))', objectPosition: 'center' }}
                    />
                  </div>
                  {/* Content */}
                  <div className="text-center md:text-left">
                    <p className="text-lg mb-6" style={{ color: '#e5e5e5' }}>
                      A complete foundation for aspiring DJs looking to mix confidently, understand music structure and perform with clarity.
                    </p>
                    <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-8">
                      {["30 Video Lessons", "Daily Interactive Quizzes", "Practice Files and Resources", "Certificate of Achievement"].map((item, index) => (
                        <span key={index} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(212, 175, 55, 0.15)', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                          {item}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-col items-center md:items-start gap-4">
                      <Link to="/pricing?purchase=course" data-testid="course-cta">
                        <Button size="lg" className="rounded-full transition-all duration-300 text-lg px-8" style={{ background: '#D4AF37', color: '#0a0a0a', fontWeight: '600', boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)' }}>
                          Get Started - £45.99
                        </Button>
                      </Link>
                      <span className="text-sm" style={{ color: '#a3a3a3' }}>One-time payment • Lifetime access to the Starter Membership</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: TESTIMONIALS */}
      <section className="py-20" data-testid="testimonials-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>What Our Students Say</h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#a3a3a3' }}>
              Join hundreds of satisfied DJs who transformed their skills with Blendonics
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:scale-105 hover:z-10 hover:shadow-lg hover:shadow-yellow-500/20">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="#D4AF37" style={{ color: '#D4AF37' }} />
                  ))}
                </div>
                <div className="mb-4">
                  <Quote size={24} style={{ color: '#D4AF37', opacity: 0.5 }} />
                </div>
                <p className="mb-6 text-lg" style={{ color: '#ffffff' }}>"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold" style={{ color: '#ffffff' }}>{testimonial.name}</p>
                  <p className="text-sm" style={{ color: '#a3a3a3' }}>{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-12 md:gap-20">
            {[
              { value: "100+", label: "Students Enrolled" },
              { value: "5/5", label: "Average Rating" },
              { value: "30+", label: "Countries" },
              { value: "95%", label: "Completion Rate" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold" style={{ color: '#D4AF37' }}>{stat.value}</p>
                <p className="text-sm mt-1" style={{ color: '#a3a3a3' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-card/30" data-testid="cta-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto glass-panel rounded-3xl p-12 text-center border-2" style={{ borderColor: '#D4AF37', boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)' }}>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>Ready to Level Up?</h2>
            <p className="mb-8 max-w-2xl mx-auto" style={{ color: '#a3a3a3' }}>
              Join Blendonics today and get access to world-class DJ education, community, and resources.
            </p>
            <Link to="/signup" data-testid="cta-button">
              <Button size="lg" className="rounded-full transition-all duration-300" style={{ background: '#D4AF37', color: '#141414', boxShadow: '0 10px 40px rgba(212, 175, 55, 0.5)', fontWeight: '600' }}>
                Get Started Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
